/**
 * Runtime validator for {@link DocumentContract}.
 * ────────────────────────────────────────────────
 * Typed as `z.ZodType<DocumentContract>`, so it stays structurally compatible
 * with the hand-written interfaces in `contract.ts` (the type source of truth).
 *
 * Every field added in schemaVersion 0.1.0 is optional, so legacy 0.0.1
 * contracts (estimate / resource-plan) validate unchanged.
 *
 * Use `documentContractSchema.parse(json)` at seed time and before rendering.
 */
import { z } from 'zod';
import { evenColumnWidths } from './contract.js';
import { sowCalculationsSchema } from '../sow-calculations.js';
/** Grid column count of a row: sum of cell column-spans (matches the renderers). */
function countGridColumns(children) {
    return (children ?? []).reduce((n, c) => n + (c.columnSpan ?? 1), 0);
}
const protossVarString = z.string();
const borderDefinition = z.object({
    style: z.enum(['single', 'dashed', 'dotted', 'none']),
    size: z.number(),
    color: protossVarString,
});
const textRunDefinition = z.object({
    text: protossVarString,
    bold: z.boolean().optional(),
    italics: z.boolean().optional(),
    underline: z.boolean().optional(),
    strike: z.boolean().optional(),
    subscript: z.boolean().optional(),
    superscript: z.boolean().optional(),
    size: z.number().optional(),
    color: protossVarString.optional(),
    font: z.string().optional(),
    break: z.number().optional(),
    highlight: z.string().optional(),
    link: protossVarString.optional(),
    anchor: protossVarString.optional(),
});
const imageRunDefinition = z.object({
    type: z.literal('image'),
    src: protossVarString,
    width: z.union([z.number(), protossVarString]),
    height: z.union([z.number(), protossVarString]),
});
const pageFieldRunDefinition = z.object({
    type: z.literal('pageField'),
    field: z.enum(['current', 'total']),
    fallback: protossVarString.optional(),
    bold: z.boolean().optional(),
    size: z.number().optional(),
    color: protossVarString.optional(),
    font: z.string().optional(),
});
const tabRunDefinition = z.object({ type: z.literal('tab') });
const footnoteRunDefinition = z.object({
    type: z.literal('footnote'),
    children: z.array(textRunDefinition),
});
// Text runs have no `type` discriminant, so it must be the last branch.
const paragraphChild = z.union([
    imageRunDefinition,
    pageFieldRunDefinition,
    tabRunDefinition,
    footnoteRunDefinition,
    textRunDefinition,
]);
const listMarker = z.object({
    kind: z.enum(['bullet', 'ordered']),
    level: z.number().optional(),
    ordinalKey: z.string().optional(),
});
const paragraphDefinition = z.object({
    type: z.literal('paragraph'),
    when: protossVarString.optional(),
    conditional: z.boolean().optional(),
    conditionalNote: z.string().optional(),
    children: z.array(paragraphChild),
    alignment: z.enum(['left', 'center', 'right', 'justify']).optional(),
    spacing: z
        .object({
        before: z.number().optional(),
        after: z.number().optional(),
        line: z.number().optional(),
        lineRule: z.enum(['auto', 'exact', 'atLeast']).optional(),
    })
        .optional(),
    indent: z
        .object({
        left: z.number().optional(),
        right: z.number().optional(),
        firstLine: z.number().optional(),
        hanging: z.number().optional(),
    })
        .optional(),
    heading: z
        .enum(['Heading1', 'Heading2', 'Heading3', 'Heading4', 'Heading5', 'Heading6', 'Title'])
        .optional(),
    style: z.string().optional(),
    list: listMarker.optional(),
    pageBreakBefore: z.boolean().optional(),
    border: z
        .object({
        top: borderDefinition.optional(),
        bottom: borderDefinition.optional(),
        left: borderDefinition.optional(),
        right: borderDefinition.optional(),
    })
        .optional(),
    bookmark: z.string().optional(),
    shading: z.object({ fill: protossVarString }).optional(),
    tabStops: z
        .array(z.object({
        position: z.number(),
        alignment: z.enum(['left', 'center', 'right']).optional(),
        leader: z.enum(['dot', 'hyphen', 'underscore', 'none']).optional(),
    }))
        .optional(),
});
const widthSpec = z.object({
    size: z.number(),
    type: z.enum(['auto', 'pct', 'dxa']),
});
const cellMargins = z.object({
    top: z.number().optional(),
    bottom: z.number().optional(),
    left: z.number().optional(),
    right: z.number().optional(),
});
// DocumentChild is recursive (table cells contain DocumentChild), so use z.lazy.
const documentChild = z.lazy(() => z.union([
    paragraphDefinition,
    tableDefinition,
    dynamicBlockDefinition,
    pageBreakDefinition,
    signatureBlockDefinition,
    contentBlockDefinition,
    tocDefinition,
]));
const tableCellDefinition = z.lazy(() => z.object({
    children: z.array(documentChild),
    width: widthSpec.optional(),
    shading: z.object({ fill: protossVarString }).optional(),
    margins: cellMargins.optional(),
    borders: z
        .object({
        top: borderDefinition.optional(),
        bottom: borderDefinition.optional(),
        left: borderDefinition.optional(),
        right: borderDefinition.optional(),
    })
        .optional(),
    verticalAlign: z.enum(['top', 'center', 'bottom']).optional(),
    columnSpan: z.number().optional(),
    rowSpan: z.number().optional(),
}));
const tableRowDefinition = z.object({
    children: z.array(tableCellDefinition),
    tableHeader: z.boolean().optional(),
});
const tableDefinition = z
    .object({
    type: z.literal('table'),
    rows: z.array(tableRowDefinition),
    width: widthSpec.optional(),
    indent: widthSpec.optional(),
    columnWidths: z.array(z.number()).optional(),
    borders: z
        .union([
        z.boolean(),
        z.object({
            top: borderDefinition.optional(),
            bottom: borderDefinition.optional(),
            left: borderDefinition.optional(),
            right: borderDefinition.optional(),
            insideHorizontal: borderDefinition.optional(),
            insideVertical: borderDefinition.optional(),
        }),
    ])
        .optional(),
    margins: cellMargins.optional(),
})
    // Every table must carry column widths or it collapses on Google Docs export.
    // Fill an even split when absent/mismatched; hand-tuned widths are preserved.
    .transform((t) => {
    const cols = countGridColumns(t.rows[0]?.children);
    return cols > 0 && (!t.columnWidths || t.columnWidths.length !== cols)
        ? { ...t, columnWidths: evenColumnWidths(cols) }
        : t;
});
const dynamicBlockDefinition = z
    .object({
    type: z.literal('dynamicBlock'),
    when: protossVarString.optional(),
    conditional: z.boolean().optional(),
    conditionalNote: z.string().optional(),
    dataKey: z.string(),
    rowTemplate: tableRowDefinition,
    headerRows: z.array(tableRowDefinition).optional(),
    columnWidths: z.array(z.number()).optional(),
    // Free-text editable in the prose editor; data tables default read-only (see contract.ts).
    editable: z.boolean().optional(),
})
    // Column count comes from the header row first, else the data-row template -
    // matching the renderer's `numCols`.
    .transform((b) => {
    const cols = countGridColumns((b.headerRows?.[0] ?? b.rowTemplate)?.children);
    return cols > 0 && (!b.columnWidths || b.columnWidths.length !== cols)
        ? { ...b, columnWidths: evenColumnWidths(cols) }
        : b;
});
const pageBreakDefinition = z.object({
    type: z.literal('pageBreak'),
});
const signatureFieldKey = z.enum(['company', 'name', 'title', 'signature', 'date']);
const signaturePartyDefinition = z.object({
    label: protossVarString.optional(),
    fields: z.array(signatureFieldKey),
    values: z
        .object({
        company: protossVarString.optional(),
        name: protossVarString.optional(),
        title: protossVarString.optional(),
        date: protossVarString.optional(),
    })
        .partial()
        .optional(),
    anchors: z
        .object({
        signature: protossVarString.optional(),
        date: protossVarString.optional(),
        name: protossVarString.optional(),
        title: protossVarString.optional(),
    })
        .partial()
        .optional(),
});
const signatureBlockDefinition = z.object({
    type: z.literal('signatureBlock'),
    parties: z.array(signaturePartyDefinition),
    layout: z.enum(['columns', 'stacked']).optional(),
    mode: z.enum(['visual', 'anchors', 'visual+anchors']).optional(),
    fieldLabels: z
        .object({
        company: z.string().optional(),
        name: z.string().optional(),
        title: z.string().optional(),
        signature: z.string().optional(),
        date: z.string().optional(),
    })
        .partial()
        .optional(),
});
const contentBlockDefinition = z.object({
    type: z.literal('contentBlock'),
    dataKey: z.string(),
    when: protossVarString.optional(),
    source: z.enum(['fixed', 'ai', 'document']).optional(),
    // Static fallback markdown when the resolved narrative is empty (see ContentBlockDefinition).
    default: z.string().optional(),
    conditional: z.boolean().optional(),
    conditionalNote: z.string().optional(),
});
const tocDefinition = z.object({
    type: z.literal('toc'),
    title: protossVarString.optional(),
    maxLevel: z.number().optional(),
    entryFont: z.string().optional(),
    entrySize: z.number().optional(),
    entryColor: protossVarString.optional(),
    titleFont: z.string().optional(),
    titleColor: protossVarString.optional(),
});
const headerFooterDefinition = z.object({
    children: z.array(z.union([paragraphDefinition, tableDefinition])),
});
const sectionDefinition = z.object({
    properties: z
        .object({
        page: z
            .object({
            margin: z
                .object({
                top: z.number().optional(),
                right: z.number().optional(),
                bottom: z.number().optional(),
                left: z.number().optional(),
            })
                .optional(),
            size: z
                .object({
                width: z.number().optional(),
                height: z.number().optional(),
                orientation: z.enum(['portrait', 'landscape']).optional(),
            })
                .optional(),
            columns: z
                .object({
                count: z.number(),
                space: z.number().optional(),
                equalWidth: z.boolean().optional(),
            })
                .optional(),
        })
            .optional(),
        titlePage: z.boolean().optional(),
        verticalAlign: z.enum(['top', 'center', 'bottom']).optional(),
        coverBackground: protossVarString.optional(),
        headers: z
            .object({
            default: headerFooterDefinition.optional(),
            first: headerFooterDefinition.optional(),
            even: headerFooterDefinition.optional(),
        })
            .optional(),
        footers: z
            .object({
            default: headerFooterDefinition.optional(),
            first: headerFooterDefinition.optional(),
            even: headerFooterDefinition.optional(),
        })
            .optional(),
    })
        .optional(),
    children: z.array(documentChild),
});
const exprRowMap = z.record(z.string());
const rowBinding = z.object({
    for: z.string(),
    where: z.string().optional(),
    row: exprRowMap,
    append: z.array(exprRowMap).optional(),
});
const staticRowsBinding = z.object({ rows: z.array(exprRowMap) });
const dataBinding = z.union([rowBinding, staticRowsBinding]);
const documentBindings = z.object({
    context: z.record(z.string()).optional(),
    data: z.record(dataBinding).optional(),
    narrative: z
        .object({
        from: z.string(),
        splitByHeading: z.boolean().optional(),
        splitMaxLevel: z.number().optional(),
        headingColor: protossVarString.optional(),
        headingColorMaxLevel: z.number().optional(),
        headingColorAfterMaxLevel: protossVarString.optional(),
        headingBoldMaxLevel: z.number().optional(),
        headingSizeAfterMaxLevel: z.number().optional(),
        headingShading: protossVarString.optional(),
        tableHeaderFill: protossVarString.optional(),
        tableHeaderColor: protossVarString.optional(),
    })
        .optional(),
    calc: sowCalculationsSchema.optional(),
});
export const documentContractSchema = z.object({
    provider: z.literal('docx'),
    schemaVersion: z.string(),
    output: z.object({ filename: protossVarString }),
    document: z.object({
        creator: protossVarString.optional(),
        title: protossVarString.optional(),
        description: protossVarString.optional(),
        defaultFont: z.string().optional(),
        defaultSize: z.number().optional(),
        defaultLineSpacing: z.number().optional(),
        defaultSpaceAfter: z.number().optional(),
        background: protossVarString.optional(),
        watermark: z
            .object({ text: protossVarString.optional(), image: protossVarString.optional() })
            .optional(),
    }),
    bindings: documentBindings.optional(),
    retiredNarrativeSlugs: z.array(z.string()).optional(),
    sections: z.array(sectionDefinition),
});
/** Parse + validate an unknown value as a DocumentContract (throws on mismatch). */
export function parseDocumentContract(value) {
    return documentContractSchema.parse(value);
}
//# sourceMappingURL=contract.zod.js.map